import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import API_URL from '../config';
import { useNavigate } from 'react-router-dom';
import SharedNav from './SharedNav';
import './StationMap.css';

const DIFFICULTIES = ['Very Difficult', 'Difficult', 'Difficult/Moderate', 'Moderate', 'Easy', 'Unknown'];

const COLORS = {
  'Very Difficult':    '#991b1b',
  'Difficult':         '#ea580c',
  'Difficult/Moderate':'#f59e0b',
  'Moderate':          '#2563eb',
  'Easy':              '#16a34a',
  'Unknown':           '#6b7280',
};

const EMPTY_FORM = {
  region: '', state: '', city: '', abbreviation: '',
  station_number: '', difficulty: 'Moderate', lat: '', lng: '', street_address: '',
};

function MapClickHandler({ onMapClick, placingPin }) {
  useMapEvents({
    click(e) {
      if (placingPin) onMapClick(e.latlng);
    },
  });
  return null;
}

export default function StationMap({ user, onLogout }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };
  const isAdminOrRecruiter = ['admin', 'recruiter', 'recruiting_admin'].includes(user?.role?.toLowerCase());

  const [stations, setStations] = useState([]);
  const [search, setSearch] = useState('');
  const [highlightId, setHighlightId] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('edit'); // 'edit' | 'create'
  const [form, setForm] = useState(EMPTY_FORM);
  const [placingPin, setPlacingPin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const popupRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/map-stations`, { headers: authHeaders });
      setStations(res.data);
    } catch (e) {
      console.error('Failed to load stations', e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const searchLower = search.toLowerCase();
  const visible = stations.filter(s => {
    const matchSearch = !search ||
      s.city.toLowerCase().includes(searchLower) ||
      String(s.station_number).includes(search) ||
      (s.abbreviation || '').toLowerCase().includes(searchLower);
    const matchDiff = !filterDifficulty || s.difficulty === filterDifficulty;
    return matchSearch && matchDiff;
  });

  const handleSearchSelect = (s) => {
    setHighlightId(s.id);
    setSelectedStation(s);
  };

  const openEdit = (s) => {
    setForm({
      region: s.region || '',
      state: s.state || '',
      city: s.city || '',
      abbreviation: s.abbreviation || '',
      station_number: s.station_number || '',
      difficulty: s.difficulty || 'Moderate',
      lat: s.lat || '',
      lng: s.lng || '',
      street_address: s.street_address || '',
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setModalMode('create');
    setPlacingPin(false);
    setShowModal(true);
  };

  const handleMapClick = (latlng) => {
    setForm(f => ({ ...f, lat: latlng.lat.toFixed(6), lng: latlng.lng.toFixed(6) }));
    setPlacingPin(false);
  };

  const handleSave = async () => {
    if (!form.city && !form.station_number) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        station_number: form.station_number ? parseInt(form.station_number) : null,
        lat: form.lat !== '' ? parseFloat(form.lat) : null,
        lng: form.lng !== '' ? parseFloat(form.lng) : null,
      };
      if (modalMode === 'edit') {
        await axios.put(`${API_URL}/api/map-stations/${selectedStation.id}`, payload, { headers: authHeaders });
      } else {
        await axios.post(`${API_URL}/api/map-stations`, payload, { headers: authHeaders });
      }
      await load();
      setShowModal(false);
      setSelectedStation(null);
    } catch (e) {
      console.error('Save failed', e);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${selectedStation.city}?`)) return;
    await axios.delete(`${API_URL}/api/map-stations/${selectedStation.id}`, { headers: authHeaders });
    await load();
    setShowModal(false);
    setSelectedStation(null);
  };

  const geocodeCity = async () => {
    if (!form.city || !form.state) return;
    const q = `${form.city}, ${form.state}, USA`;
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1&countrycodes=us`, {
        headers: { 'User-Agent': 'mountain-recruiting-crm/1.0' }
      });
      const data = await res.json();
      if (data[0]) setForm(f => ({ ...f, lat: parseFloat(data[0].lat).toFixed(6), lng: parseFloat(data[0].lon).toFixed(6) }));
    } catch (e) { console.error('Geocode failed', e); }
  };

  const geocodeFullAddress = async () => {
    if (!form.city || !form.state) return;
    const parts = [form.street_address, form.city, form.state, 'USA'].filter(Boolean);
    const q = parts.join(', ');
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1&countrycodes=us`, {
        headers: { 'User-Agent': 'mountain-recruiting-crm/1.0' }
      });
      const data = await res.json();
      if (data[0]) setForm(f => ({ ...f, lat: parseFloat(data[0].lat).toFixed(6), lng: parseFloat(data[0].lon).toFixed(6) }));
      else alert('Address not found — try adjusting the street address or city/state.');
    } catch (e) { console.error('Geocode failed', e); }
  };

  const searchResults = search.length >= 1
    ? stations
        .filter(s =>
          s.city.toLowerCase().includes(searchLower) ||
          String(s.station_number).includes(search) ||
          (s.abbreviation || '').toLowerCase().includes(searchLower)
        )
        .sort((a, b) => {
          // Exact station number match floats to top
          const aExact = String(a.station_number) === search;
          const bExact = String(b.station_number) === search;
          if (aExact && !bExact) return -1;
          if (!aExact && bExact) return 1;
          // Then prefix matches
          const aPrefix = String(a.station_number).startsWith(search);
          const bPrefix = String(b.station_number).startsWith(search);
          if (aPrefix && !bPrefix) return -1;
          if (!aPrefix && bPrefix) return 1;
          return a.station_number - b.station_number;
        })
        .slice(0, 10)
    : [];

  return (
    <div className="station-map-page">
      {/* Header */}
      <SharedNav user={user} onLogout={onLogout} />

      {/* Toolbar */}
      <div className="map-toolbar">
        <div className="map-search-wrap">
          <input
            className="map-search"
            placeholder="Search by city or station number..."
            value={search}
            onChange={e => { setSearch(e.target.value); setHighlightId(null); }}
          />
          {searchResults.length > 0 && (
            <div className="map-search-results">
              {searchResults.map(s => (
                <div key={s.id} className="map-search-item" onClick={() => { handleSearchSelect(s); setSearch(''); }}>
                  <span className="dot" style={{ background: COLORS[s.difficulty] || COLORS.Unknown }} />
                  <strong>{s.city}</strong>
                  <span className="station-num">#{s.station_number}</span>
                  <span className="state-label">{s.state}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <select className="map-filter" value={filterDifficulty} onChange={e => setFilterDifficulty(e.target.value)}>
          <option value="">All Difficulties</option>
          {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
        </select>

        {isAdminOrRecruiter && (
          <button className="map-btn-add" onClick={openCreate}>+ Add Station</button>
        )}

        {selectedStation && isAdminOrRecruiter && (
          <button className="map-btn-edit" onClick={() => openEdit(selectedStation)}>
            ✏️ Edit: {selectedStation.city}
          </button>
        )}
      </div>

      {/* Map */}
      <div className={`map-wrap${placingPin ? ' placing-pin' : ''}`}>
        <MapContainer center={[39.5, -98.35]} zoom={5} className="leaflet-map">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onMapClick={handleMapClick} placingPin={placingPin} />
          {visible.filter(s => s.lat != null && s.lng != null).map(s => {
            const isHighlit = s.id === highlightId;
            const color = COLORS[s.difficulty] || COLORS.Unknown;
            return (
              <CircleMarker
                key={s.id}
                center={[parseFloat(s.lat), parseFloat(s.lng)]}
                radius={isHighlit ? 12 : 7}
                pathOptions={{
                  color: isHighlit ? '#fff' : color,
                  fillColor: color,
                  fillOpacity: 0.92,
                  weight: isHighlit ? 3 : 1.5,
                }}
                eventHandlers={{
                  click: () => setSelectedStation(s),
                }}
              >
                <Popup>
                  <div className="map-popup">
                    <div className="popup-city">{s.city}</div>
                    <div className="popup-state">{s.state}</div>
                    {s.station_number && <div className="popup-num">Station #{s.station_number}</div>}
                    {s.abbreviation && <div className="popup-abv">{s.abbreviation}</div>}
                    <div className="popup-diff" style={{ color }}>● {s.difficulty}</div>
                    {isAdminOrRecruiter && (
                      <button className="popup-edit-btn" onClick={() => openEdit(s)}>Edit</button>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>

        {/* Legend */}
        <div className="map-legend">
          <div className="legend-title">Difficulty</div>
          {DIFFICULTIES.filter(d => d !== 'Unknown').map(d => (
            <div key={d} className="legend-item">
              <span className="legend-dot" style={{ background: COLORS[d] }} />
              {d}
            </div>
          ))}
          <div className="legend-item">
            <span className="legend-dot" style={{ background: COLORS.Unknown }} />
            Unknown
          </div>
          <div className="legend-count">{visible.length} stations</div>
        </div>

        {placingPin && (
          <div className="placing-pin-banner">Click on the map to place the pin</div>
        )}
      </div>

      {/* Edit / Create Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="modal">
            <h3>{modalMode === 'edit' ? 'Edit Station' : 'Add New Station'}</h3>
            <div className="modal-grid">
              <label>City
                <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
              </label>
              <label>State
                <input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} />
              </label>
              <label className="modal-full-row">Street Address
                <input value={form.street_address} onChange={e => setForm(f => ({ ...f, street_address: e.target.value }))} placeholder="e.g. 1234 Industrial Blvd" />
              </label>
              <label>Region
                <input value={form.region} onChange={e => setForm(f => ({ ...f, region: e.target.value }))} />
              </label>
              <label>Abbreviation
                <input value={form.abbreviation} onChange={e => setForm(f => ({ ...f, abbreviation: e.target.value }))} />
              </label>
              <label>Station #
                <input type="number" value={form.station_number} onChange={e => setForm(f => ({ ...f, station_number: e.target.value }))} />
              </label>
              <label>Difficulty
                <select value={form.difficulty} onChange={e => setForm(f => ({ ...f, difficulty: e.target.value }))}>
                  {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </label>
              <label>Latitude
                <input type="number" step="any" value={form.lat} onChange={e => setForm(f => ({ ...f, lat: e.target.value }))} />
              </label>
              <label>Longitude
                <input type="number" step="any" value={form.lng} onChange={e => setForm(f => ({ ...f, lng: e.target.value }))} />
              </label>
            </div>
            <div className="modal-coord-actions">
              <button className="btn-outline" onClick={geocodeCity} type="button">
                🌐 Geocode from City + State
              </button>
              <button className="btn-outline" onClick={geocodeFullAddress} type="button">
                📌 Geocode from Street + City + State
              </button>
              <button className="btn-outline" onClick={() => { setShowModal(false); setPlacingPin(true); }} type="button">
                📍 Click on Map
              </button>
            </div>
            <div className="modal-actions">
              {modalMode === 'edit' && (
                <button className="btn-danger-sm" onClick={handleDelete}>Delete</button>
              )}
              <button className="btn-secondary-sm" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn-primary-sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
